import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, PhoneIncoming, RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const SELECT = 'w-full h-9 px-3 text-sm rounded-md border border-slate-200 bg-white';
const STATUS_STYLE = {
  live: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-700',
  conflict: 'bg-red-50 text-red-700',
  error: 'bg-red-50 text-red-700',
};

const errorText = (error, fallback) => error?.response?.data?.detail || fallback;

/**
 * Every number answered by an agent, across accounts. Admins assign for a
 * client, import a hand-made LiveKit trunk + rule into a route, or release one.
 * Core's reconciler turns routes into LiveKit trunks and rules within ~30s.
 */
export default function InboundRoutesDashboard() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [voices, setVoices] = useState([]);

  const [clientId, setClientId] = useState('');
  const [options, setOptions] = useState({ numbers: [], agents: [] });
  const [assign, setAssign] = useState({ did_number_id: '', agent_id: '', version_id: '', voice: '' });
  const [importDraft, setImportDraft] = useState({ number: '', trunk_id: '', dispatch_rule_id: '' });
  const [busy, setBusy] = useState('');

  const loadRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/api/v1/admin/inbound-routes`, { headers });
      setRoutes(res.data?.routes || []);
    } catch (error) {
      toast.error(errorText(error, 'Failed to load inbound routes'));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadRoutes();
    Promise.all([
      axios.get(`${apiUrl}/api/v1/admin/users`, { headers }),
      axios.get(`${apiUrl}/api/v1/admin/voices`, { headers }),
    ])
      .then(([usersRes, voicesRes]) => {
        setUsers(usersRes.data?.users || []);
        setVoices((voicesRes.data?.voices || []).filter((voice) => voice.active && voice.provider));
      })
      .catch((error) => toast.error(errorText(error, 'Failed to load users and voices')));
  }, [headers, loadRoutes]);

  useEffect(() => {
    setOptions({ numbers: [], agents: [] });
    setAssign({ did_number_id: '', agent_id: '', version_id: '', voice: '' });
    if (!clientId) return;
    axios
      .get(`${apiUrl}/api/v1/admin/inbound-routes/clients/${encodeURIComponent(clientId)}/options`, { headers })
      .then((res) => setOptions(res.data))
      .catch((error) => toast.error(errorText(error, 'Failed to load this client\'s numbers and agents')));
  }, [clientId, headers]);

  const selectedAgent = options.agents.find((agent) => agent.agent_id === assign.agent_id);

  const submitAssign = async () => {
    try {
      setBusy('assign');
      await axios.put(
        `${apiUrl}/api/v1/admin/inbound-routes/clients/${encodeURIComponent(clientId)}/numbers/${encodeURIComponent(assign.did_number_id)}`,
        { agent_id: assign.agent_id, version_id: assign.version_id, voice: assign.voice },
        { headers },
      );
      toast.success('Route saved; LiveKit is updated within a minute');
      await loadRoutes();
    } catch (error) {
      toast.error(errorText(error, 'Failed to assign'));
    } finally {
      setBusy('');
    }
  };

  const submitImport = async () => {
    try {
      setBusy('import');
      await axios.post(`${apiUrl}/api/v1/admin/inbound-routes/import`, importDraft, { headers });
      toast.success('Imported; the next pass takes the trunk and rule over');
      setImportDraft({ number: '', trunk_id: '', dispatch_rule_id: '' });
      await loadRoutes();
    } catch (error) {
      toast.error(errorText(error, 'Import failed'));
    } finally {
      setBusy('');
    }
  };

  const release = async (route) => {
    if (!window.confirm(`Release ${route.number}? Its LiveKit trunk and rule are deleted and calls stop being answered.`)) return;
    try {
      setBusy(route._id);
      await axios.delete(`${apiUrl}/api/v1/admin/inbound-routes/${encodeURIComponent(route._id)}`, { headers });
      toast.success(`${route.number} released`);
      await loadRoutes();
    } catch (error) {
      toast.error(errorText(error, 'Release failed'));
    } finally {
      setBusy('');
    }
  };

  const userLabel = (id) => {
    const user = users.find((u) => u.id === id);
    return user ? `${user.name || user.email} (${user.email})` : id;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <PhoneIncoming className="w-6 h-6" /> Inbound Routes
          </h1>
          <p className="text-sm text-slate-500">
            One agent version per number. First account to assign a number keeps it until it is released.
          </p>
        </div>
        <Button variant="outline" onClick={loadRoutes} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="p-4 border rounded-xl border-slate-200 bg-white space-y-3">
          <h2 className="font-semibold text-slate-900">Assign for a client</h2>
          <div className="space-y-1">
            <Label>Client</Label>
            <select className={SELECT} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select a client</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.name || user.email} ({user.email})</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Number (granted to this client)</Label>
            <select className={SELECT} value={assign.did_number_id} disabled={!clientId}
              onChange={(e) => setAssign({ ...assign, did_number_id: e.target.value })}>
              <option value="">Select a number</option>
              {options.numbers.map((n) => <option key={n.did_number_id} value={n.did_number_id}>{n.number}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Agent</Label>
              <select className={SELECT} value={assign.agent_id} disabled={!clientId}
                onChange={(e) => {
                  const agent = options.agents.find((a) => a.agent_id === e.target.value);
                  setAssign({ ...assign, agent_id: e.target.value, version_id: agent?.current_version || agent?.versions?.[0]?.version_id || '' });
                }}>
                <option value="">Select an agent</option>
                {options.agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.agent_name || a.agent_id}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Version</Label>
              <select className={SELECT} value={assign.version_id} disabled={!selectedAgent}
                onChange={(e) => setAssign({ ...assign, version_id: e.target.value })}>
                <option value="">Select a version</option>
                {(selectedAgent?.versions || []).map((v) => (
                  <option key={v.version_id} value={v.version_id}>{v.version_name || v.version_id}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Voice</Label>
            <select className={SELECT} value={assign.voice} onChange={(e) => setAssign({ ...assign, voice: e.target.value })}>
              <option value="">Select a voice</option>
              {voices.map((v) => <option key={v.local_voice_name} value={v.local_voice_name}>{v.local_voice_name} ({v.provider})</option>)}
            </select>
          </div>
          <Button onClick={submitAssign}
            disabled={busy === 'assign' || !clientId || !assign.did_number_id || !assign.agent_id || !assign.version_id || !assign.voice}>
            {busy === 'assign' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Assign
          </Button>
        </section>

        <section className="p-4 border rounded-xl border-slate-200 bg-white space-y-3">
          <h2 className="font-semibold text-slate-900">Import a hand-made LiveKit trunk</h2>
          <p className="text-sm text-slate-500">
            For numbers wired by hand before routes existed (their route shows a conflict). Assign the number
            first, then import its trunk and dispatch rule so the route takes them over instead of conflicting.
          </p>
          <div className="space-y-1">
            <Label>Number</Label>
            <Input value={importDraft.number} placeholder="+919429394797"
              onChange={(e) => setImportDraft({ ...importDraft, number: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Trunk ID</Label>
              <Input value={importDraft.trunk_id} placeholder="ST_..."
                onChange={(e) => setImportDraft({ ...importDraft, trunk_id: e.target.value.trim() })} />
            </div>
            <div className="space-y-1">
              <Label>Dispatch rule ID</Label>
              <Input value={importDraft.dispatch_rule_id} placeholder="SDR_..."
                onChange={(e) => setImportDraft({ ...importDraft, dispatch_rule_id: e.target.value.trim() })} />
            </div>
          </div>
          <Button variant="outline" onClick={submitImport}
            disabled={busy === 'import' || !importDraft.number || !importDraft.trunk_id || !importDraft.dispatch_rule_id}>
            {busy === 'import' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Import
          </Button>
        </section>
      </div>

      <section className="border rounded-xl border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="px-4 py-3">Number</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Agent @ version</th>
              <th className="px-4 py-3">Voice</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">LiveKit</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {routes.map((route) => (
              <tr key={route._id}>
                <td className="px-4 py-3 font-mono">
                  {route.number}
                  <div className="text-xs text-slate-400">{route.channel}</div>
                </td>
                <td className="px-4 py-3 text-xs">{userLabel(route.client_id)}</td>
                <td className="px-4 py-3 font-mono text-xs">{route.agent_id} @ {route.version_id}</td>
                <td className="px-4 py-3 text-xs">{route.voice}</td>
                <td className="px-4 py-3 text-xs max-w-xs">
                  <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_STYLE[route.status] || 'bg-slate-100 text-slate-600'}`}>
                    {route.status || 'pending'}
                  </span>
                  {route.status_detail && <div className="mt-1 text-red-600">{route.status_detail}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">
                  {route.livekit?.trunk_id || '—'}<br />{route.livekit?.dispatch_rule_id || ''}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => release(route)} disabled={busy === route._id}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && routes.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No numbers are answered by an agent yet.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
